import { useEffect, useMemo, useState } from "react";
import {
  BoxSearch,
  BoxTick,
  Cloud,
  CloudPlus,
  Coin1,
  ReceiptText,
  TickCircle,
  Timer1,
} from "iconsax-react";
import { useTranslation } from "react-i18next";

import DBButton from "../../../component/global/DBButton";
import { get } from "../../../controlers/global/ConfigRequest";
import { COMPANY_DETAIL, GET_PACKAGES } from "../../../helperData/ApiAddress";
import { ORGANIZATION_MENU_KEYS } from "../../../helperData/organizationSidebar";
import { MENU_LABEL_KEYS } from "../../Organizations/MenuAccessFields";
import PackagesPaymentStatusModal from "./packagesModal/PackagesPaymentStatusModal";
import PackagesPaymentModal from "./packagesModal/PackagesPaymentModal";
import PackagesReportModal from "./packagesModal/packagesReportModal";
import PackagesBuyVolumeModal from "./packagesModal/packagesBuyVolumeModal";

const DURATION_OPTIONS = [
  { days: 30, labelKey: "Organizational.Profile.Packages.Duration.Month" },
  { days: 180, labelKey: "Organizational.Profile.Packages.Duration.SixMonths" },
  { days: 365, labelKey: "Organizational.Profile.Packages.Duration.Year" },
];

const MENU_PERMISSION = {
  reports: "report_permission",
  messages: "automation_permission",
  survey: "survey_permission",
  settings: "setting_permission",
  customer_club: "club_permission",
  restaurant: "menu_permission",
};

const initialPaymentModal = {
  show: false,
  data: {},
};

const initialPaymentStatusModal = {
  show: false,
  status: false,
  data: {},
};

const findPackagePrice = (pkg, days) =>
  (pkg?.prices || []).find(
    (item) => Number(item.duration_as_days) === Number(days)
  ) || null;

const remainingDays = (endIso) => {
  if (!endIso) return null;
  const ms = new Date(endIso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
};

const formatAmount = (value, locale) =>
  Number(value || 0).toLocaleString(locale === "fa" ? "fa-IR" : "en-US");

const monthsForDuration = (days) => {
  const value = Number(days);
  if (value === 365) return 12;
  if (value === 180) return 6;
  if (value === 30) return 1;
  if (!value) return 0;
  return Math.max(1, Math.round(value / 30));
};

const currentTone = (company) => {
  if (!company?.package) return "none";
  if (company.package_is_base) return "base";
  if (company.package_is_default && company.is_package_valid) return "trial";
  if (company.is_package_valid) return "active";
  return "expired";
};

const catalogResources = (pkg, t, locale, durationDays) => {
  const resources = [
    {
      key: "storage",
      icon: Cloud,
      value: formatAmount(pkg.storage_volume, locale),
      label: t("Organizational.Profile.Packages.Catalog.StorageUnit"),
    },
  ];
  const monthly = Number(pkg.coins) || 0;
  if (monthly > 0) {
    resources.push({
      key: "coins",
      icon: Coin1,
      value: formatAmount(monthly, locale),
      label: t("Organizational.Profile.Packages.Catalog.MonthlyCoins"),
    });
    const months = monthsForDuration(durationDays);
    if (months > 1) {
      resources.push({
        key: "termCoins",
        icon: Coin1,
        value: formatAmount(monthly * months, locale),
        label: t("Organizational.Profile.Packages.Catalog.GiftCoinsForTerm"),
      });
    }
  }
  return resources;
};

const catalogModules = (pkg, t) =>
  ORGANIZATION_MENU_KEYS.filter((key) => {
    const enabled =
      pkg.menu_access?.[key]?.enabled === true ||
      pkg[MENU_PERMISSION[key]] === true;
    return enabled && MENU_LABEL_KEYS[key];
  }).map((key) => t(MENU_LABEL_KEYS[key]));

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [company, setCompany] = useState(null);
  const [duration, setDuration] = useState(30);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(false);
  const [buyVolume, setBuyVolume] = useState(false);
  const [paymentModal, setPaymentModal] = useState(initialPaymentModal);
  const [paymentStatusModal, setPaymentStatusModal] = useState(
    initialPaymentStatusModal
  );
  const { t, i18n } = useTranslation();

  const loadCompany = () => {
    const companyId = sessionStorage.getItem("usercompany");
    if (!companyId) {
      setCompany(null);
      return Promise.resolve();
    }
    return get(COMPANY_DETAIL.replace("{id}", companyId)).then((res) => {
      setCompany(res);
    });
  };

  const loadCatalog = () =>
    get(GET_PACKAGES, { language: i18n.language }).then((res) => {
      setPackages(Array.isArray(res) ? res : []);
    });

  useEffect(() => {
    setLoading(true);
    Promise.all([loadCatalog(), loadCompany()]).finally(() => {
      setLoading(false);
    });
  }, [i18n.language]);

  const currentPackageId = company?.package ? Number(company.package) : null;
  const daysLeft = remainingDays(company?.package_end_time);
  const tone = currentTone(company);

  const currentStatusLabel = useMemo(() => {
    if (!company?.package) {
      return t("Organizational.Profile.Packages.Current.None");
    }
    if (company.package_is_base) {
      return t("Organizational.Profile.Packages.Current.Base");
    }
    if (company.package_is_default && company.is_package_valid) {
      return t("Organizational.Profile.Packages.Current.Trial");
    }
    if (company.is_package_valid) {
      return t("Organizational.Profile.Packages.Current.Active");
    }
    return t("Organizational.Profile.Packages.Current.Expired");
  }, [company, t]);

  const openPayment = (pkg) => {
    const price = findPackagePrice(pkg, duration);
    if (!price || Number(price.price) <= 0) return;
    setPaymentModal({
      show: true,
      data: {
        id: pkg.id,
        name: pkg.name,
        price,
      },
    });
  };

  const catalogActions = (
    <div className="org-packages-intro-actions">
      <DBButton
        type="secondary"
        outlined
        className="org-packages-ghost-btn"
        caption={
          <>
            <ReceiptText size={18} aria-hidden="true" />
            {t("Organizational.Profile.Packages.History")}
          </>
        }
        onClick={() => setReport(true)}
      />
      <DBButton
        type="primary"
        outlined
        className="org-packages-ghost-btn"
        caption={
          <>
            <CloudPlus size={18} aria-hidden="true" />
            {t("Organizational.Profile.Packages.BuyVolume")}
          </>
        }
        onClick={() => setBuyVolume(true)}
      />
    </div>
  );

  if (loading) {
    return (
      <div
        className="page-load-fade org-packages-catalog"
        aria-busy="true"
        aria-live="polite"
      >
        <div className="org-packages-skel org-packages-skel-intro" />
        <div className="org-packages-skel org-packages-skel-status" />
        <div className="org-packages-skel org-packages-skel-bar" />
        <div className="org-packages-grid">
          <div className="org-packages-skel org-packages-skel-card" />
          <div className="org-packages-skel org-packages-skel-card" />
          <div className="org-packages-skel org-packages-skel-card" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-load-fade">
      <div className="org-packages-catalog">
        <section className="org-packages-intro">
          <div className="org-packages-intro-copy">
            <h3 className="org-packages-title">
              {t("Organizational.Profile.Packages.Title")}
            </h3>
            <p className="org-packages-subtitle">
              {t("Organizational.Profile.Packages.Subtitle")}
            </p>
          </div>
          {catalogActions}
        </section>

        <section
          className={`org-packages-current is-${tone}`}
          aria-live="polite"
        >
          <div className="org-packages-current-icon" aria-hidden="true">
            <BoxTick size={22} />
          </div>
          <div className="org-packages-current-copy">
            <p className="org-packages-kicker">
              {t("Organizational.Profile.Packages.Current.Label")}
            </p>
            <h2>
              {company?.package_name ||
                t("Organizational.Profile.Packages.Current.None")}
            </h2>
            <span className={`org-packages-status-badge is-${tone}`}>
              {currentStatusLabel}
            </span>
          </div>
          {company?.package &&
          !company.package_is_base &&
          daysLeft != null &&
          daysLeft > 0 ? (
            <div className="org-packages-remaining">
              <Timer1 size={16} aria-hidden="true" />
              <span className="org-packages-remaining-value">{daysLeft}</span>
              <span>{t("Organizational.Profile.Packages.Current.DaysLeft")}</span>
            </div>
          ) : null}
        </section>

        <div className="org-packages-toolbar">
          <p className="org-packages-toolbar-label" id="package-duration-label">
            {t("Organizational.Profile.Packages.Duration.Label")}
          </p>
          <div
            className="org-packages-durations"
            role="radiogroup"
            aria-labelledby="package-duration-label"
          >
            {DURATION_OPTIONS.map((option) => {
              const selected = Number(duration) === option.days;
              return (
                <button
                  key={option.days}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={selected ? "is-active" : ""}
                  onClick={() => setDuration(option.days)}
                >
                  {t(option.labelKey)}
                </button>
              );
            })}
          </div>
        </div>

        {packages.length === 0 ? (
          <div className="org-packages-empty">
            <BoxSearch size={28} aria-hidden="true" />
            <p>{t("Organizational.Profile.Packages.Catalog.Empty")}</p>
          </div>
        ) : (
          <div className="org-packages-grid">
            {packages.map((pkg) => {
              const price = findPackagePrice(pkg, duration);
              const isCurrent = currentPackageId === Number(pkg.id);
              const isFree =
                Boolean(pkg.is_base) || Number(price?.price || 0) === 0;
              const canBuy = Boolean(price) && Number(price.price) > 0;
              const resources = catalogResources(
                pkg,
                t,
                i18n.language,
                duration
              );
              const modules = catalogModules(pkg, t);
              return (
                <article
                  key={pkg.id}
                  className={`org-packages-card${isCurrent ? " is-current" : ""}${
                    pkg.is_default ? " is-trial" : ""
                  }${pkg.is_base ? " is-base" : ""}`}
                >
                  <header>
                    {isCurrent || pkg.is_default || pkg.is_base ? (
                      <div className="org-packages-card-badges">
                        {isCurrent ? (
                          <span className="org-packages-badge current">
                            {t(
                              "Organizational.Profile.Packages.Catalog.Current"
                            )}
                          </span>
                        ) : null}
                        {pkg.is_default ? (
                          <span className="org-packages-badge trial">
                            {t("Organizational.Profile.Packages.Catalog.Trial")}
                          </span>
                        ) : null}
                        {pkg.is_base ? (
                          <span className="org-packages-badge base">
                            {t("Organizational.Profile.Packages.Catalog.Base")}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    <h3>{pkg.name}</h3>
                    {pkg.description ? <p>{pkg.description}</p> : null}
                  </header>
                  <div className="org-packages-price">
                    <span className="org-packages-price-value">
                      {isFree
                        ? t("Organizational.Profile.Packages.Catalog.Free")
                        : formatAmount(price?.price, i18n.language)}
                    </span>
                    {!isFree ? (
                      <span className="org-packages-price-unit">
                        {t("Organizational.Profile.Packages.Payment.Unit")}
                      </span>
                    ) : null}
                  </div>
                  <ul className="org-packages-resources">
                    {resources.map((item) => {
                      const Icon = item.icon;
                      return (
                        <li key={item.key}>
                          <span
                            className="org-packages-resource-icon"
                            aria-hidden="true"
                          >
                            <Icon size={16} />
                          </span>
                          <span className="org-packages-resource-value">
                            {item.value}
                          </span>
                          <span className="org-packages-resource-label">
                            {item.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {modules.length > 0 ? (
                    <div className="org-packages-modules">
                      <p className="org-packages-modules-label">
                        {t("Organizational.Profile.Packages.Catalog.Modules")}
                      </p>
                      <ul>
                        {modules.map((feature) => (
                          <li key={feature}>
                            <TickCircle size={14} aria-hidden="true" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {pkg.is_base ? (
                    <p className="org-packages-card-note">
                      {t("Organizational.Profile.Packages.Catalog.BaseHint")}
                    </p>
                  ) : isCurrent ? (
                    <p className="org-packages-card-current">
                      {t("Organizational.Profile.Packages.Catalog.ActiveCta")}
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="org-packages-buy"
                      disabled={!canBuy}
                      aria-disabled={!canBuy}
                      onClick={() => openPayment(pkg)}
                    >
                      {canBuy
                        ? t("Organizational.Profile.BuyPackage.Button")
                        : t(
                            "Organizational.Profile.Packages.Catalog.Unavailable"
                          )}
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>

      <PackagesPaymentModal
        show={paymentModal.show}
        data={paymentModal.data}
        onHide={() => {
          setPaymentModal(initialPaymentModal);
        }}
        onSuccess={(res) => {
          setPaymentStatusModal({
            show: true,
            status: "success",
            data: res,
          });
          loadCompany();
        }}
      />
      <PackagesPaymentStatusModal
        show={paymentStatusModal.show}
        data={paymentStatusModal.data}
        onHide={() => {
          setPaymentStatusModal(initialPaymentStatusModal);
        }}
        onClick={() => {
          if (paymentStatusModal.status) {
            window.print();
          }
        }}
        status={paymentStatusModal.status}
      />
      <PackagesReportModal
        show={report}
        onHide={() => {
          setReport(false);
        }}
      />
      <PackagesBuyVolumeModal
        show={buyVolume}
        onHide={() => {
          setBuyVolume(false);
        }}
      />
    </div>
  );
}
